import React, { useEffect, useState } from "react";
import api from "../api";
import { Mail, AlertTriangle, CheckCircle2, RefreshCw, Copy, Inbox } from "lucide-react";

/**
 * Diagnostic widget for the Resend webhook integration. Surfaces "silent
 * failures" where emails are sent but no events (delivered/opened/clicked)
 * come back because the webhook isn't pointing to the deployed URL.
 *
 * Also exposes a "Sync from Resend" button: pulls last 100 emails from
 * Resend API and reconciles them into `emails_sent` — recovers data lost
 * during a previous client-timeout on a synchronous bulk send.
 */
export default function ResendDiagnostics() {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email/webhook/diagnostics");
      setDiag(data);
    } catch (e) {
      setDiag({ error: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runSync = async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const { data } = await api.post("/email/sync-from-resend", { limit: 200, days: 14 });
      setSyncMsg({ type: "ok", text: data.message });
    } catch (e) {
      setSyncMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setSyncing(false); }
  };

  const copyUrl = () => {
    if (!diag?.expectedUrl) return;
    navigator.clipboard?.writeText(diag.expectedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="card mb-4"><span className="spinner"/></div>;
  if (!diag || diag.error) return null;

  const s = diag.last7Days || {};
  const suspect = diag.suspectMissingWebhook;

  return (
    <div className="card mb-4" data-testid="resend-diagnostics-card">
      <div className="h-section mb-2" style={{display:"flex", alignItems:"center", gap:8}}>
        <Mail size={18}/> Diagnostica Resend (ultimi 7 giorni)
        <button className="btn-icon" onClick={load} title="Aggiorna" style={{marginLeft:"auto"}}>
          <RefreshCw size={14}/>
        </button>
      </div>

      <div className="grid-stats" style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(110px, 1fr))", gap:8, marginBottom:14}}>
        <div className="mini-stat"><div className="mini-num">{s.emailsSent || 0}</div><div className="mini-lbl">Inviate</div></div>
        <div className="mini-stat mini-blue"><div className="mini-num">{s.delivered || 0}</div><div className="mini-lbl">Consegnate</div></div>
        <div className="mini-stat mini-green"><div className="mini-num">{s.opened || 0}</div><div className="mini-lbl">Aperte ({s.openRate}%)</div></div>
        <div className="mini-stat mini-violet"><div className="mini-num">{s.clicked || 0}</div><div className="mini-lbl">Click ({s.clickRate}%)</div></div>
        <div className="mini-stat mini-red"><div className="mini-num">{s.bounced || 0}</div><div className="mini-lbl">Bounce</div></div>
      </div>

      {suspect && (
        <div className="card mb-3" style={{padding:12, background:"#fef2f2", borderLeft:"4px solid #b91c1c"}}>
          <div className="text-small" style={{color:"#7f1d1d", fontWeight:700, marginBottom:6, display:"flex", alignItems:"center", gap:6}}>
            <AlertTriangle size={14}/> Webhook Resend probabilmente non configurato
          </div>
          <div className="text-small" style={{color:"#7f1d1d", lineHeight:1.5}}>
            Hai inviato {s.emailsSent} email negli ultimi 7 giorni ma nessuna risulta consegnata né aperta.
            Resend invia gli eventi a un webhook che <strong>deve puntare al dominio della produzione</strong>.
            Vai su <a href="https://resend.com/webhooks" target="_blank" rel="noreferrer"
              style={{color:"#7f1d1d", textDecoration:"underline"}}>resend.com/webhooks</a> e crea
            un endpoint con questo URL:
          </div>
          <div style={{marginTop:8, display:"flex", gap:6, alignItems:"center"}}>
            <code style={{flex:1, padding:"8px 12px", background:"#fff", borderRadius:6, fontSize:12, border:"1px solid #fecaca"}}>
              {diag.expectedUrl}
            </code>
            <button className="btn btn-sm" onClick={copyUrl} data-testid="copy-webhook-url-btn">
              <Copy size={13}/> {copied ? "Copiato!" : "Copia"}
            </button>
          </div>
          <div className="text-small text-muted" style={{marginTop:8, lineHeight:1.5}}>
            Attiva gli eventi: <code>email.delivered</code>, <code>email.opened</code>,
            <code>email.clicked</code>, <code>email.bounced</code>, <code>email.complained</code>.
            Il secret del webhook deve essere <code>RESEND_WEBHOOK_SECRET</code> già configurato nel backend.
          </div>
        </div>
      )}

      {!suspect && s.emailsSent > 0 && (
        <div className="text-small" style={{color:"#166534", display:"flex", alignItems:"center", gap:6, marginBottom:12}}>
          <CheckCircle2 size={14}/> {diag.message}
        </div>
      )}

      <div style={{borderTop:"1px solid var(--mv-border-light)", paddingTop:12, marginTop:8}}>
        <div className="text-small" style={{fontWeight:700, marginBottom:6, display:"flex", alignItems:"center", gap:6}}>
          <Inbox size={14}/> Recupero invii non tracciati
        </div>
        <div className="text-small text-muted" style={{marginBottom:8, lineHeight:1.5}}>
          Se nel passato un invio bulk si è interrotto per timeout di rete, Resend potrebbe aver effettivamente
          inviato delle email che il sistema locale non ha registrato. Questo pulsante interroga Resend e
          recupera fino a 200 invii degli ultimi 14 giorni.
        </div>
        <button className="btn btn-secondary btn-sm" onClick={runSync} disabled={syncing}
          data-testid="sync-resend-btn">
          {syncing ? <span className="spinner"/> : <><RefreshCw size={13}/> Sincronizza con Resend</>}
        </button>
        {syncMsg && (
          <div className="text-small" style={{
            marginTop:8, padding:8, borderRadius:6,
            background: syncMsg.type==="ok" ? "#dcfce7" : "#fef2f2",
            color: syncMsg.type==="ok" ? "#166534" : "#7f1d1d",
          }}>{syncMsg.text}</div>
        )}
      </div>
    </div>
  );
}
